import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
    Container, Row, Col, Card, CardBody, CardHeader,
    Badge, Spinner, Alert, Input,
    UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem,
    Modal, ModalHeader, ModalBody,
} from "reactstrap";
import CountUp from "react-countup";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import { apiV2PostConfig } from "../../helpers/api_helper";

/* ── interface ── */
interface Invoice {
    facture_id: string;
    ref: string;
    total_ht: string;
    total_tva: string;
    total_ttc: string;
    datef: string;
    fk_statut: string;
    paye: string;
    pdf?: string;
}

/* ── helpers ── */
const formatDate = (raw: string) => {
    if (!raw) return "—";
    const d = new Date(raw);
    if (isNaN(d.getTime())) return "—";
    return `${String(d.getDate()).padStart(2,"0")}/${String(d.getMonth()+1).padStart(2,"0")}/${d.getFullYear()}`;
};

const formatEur = (amount: string) =>
    parseFloat(amount || "0").toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";

/* ── composant ── */
const MesFactures = () => {
    const [invoices, setInvoices]   = useState<Invoice[]>([]);
    const [loading, setLoading]     = useState(true);
    const [error, setError]         = useState<string | null>(null);
    const [search, setSearch]       = useState("");

    const currentYear = new Date().getFullYear();
    const years       = Array.from({ length: 10 }, (_, i) => currentYear - i);
    const [year, setYear]           = useState(currentYear.toString());

    /* modal PDF */
    const [modal, setModal]                   = useState(false);
    const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);

    const openPdf = (inv: Invoice) => { setSelectedInvoice(inv); setModal(true); };

    /* ── fetch ── */
    const fetchInvoices = async (y: string) => {
        setLoading(true);
        try {
            const res: any = await axios.post("/apiV2/", { action: "invoices", year: y }, apiV2PostConfig());
            setInvoices(res?.data ?? res ?? []);
        } catch {
            setError("Erreur lors de la récupération des factures.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchInvoices(year); }, []);

    const changeYear = (y: string) => { setYear(y); fetchInvoices(y); };

    /* ── KPI ── */
    const total    = invoices.length;
    const payees   = invoices.filter(x => x.paye === "1").length;
    const impayees = invoices.filter(x => x.paye !== "1").length;
    const totalImpaye = invoices
        .filter(x => x.paye !== "1")
        .reduce((s, x) => s + parseFloat(x.total_ttc || "0"), 0);

    const widgets = [
        { label: "Total factures",  counter: total,       suffix: "",   decimals: 0, icon: "ri-file-list-3-line",      color: "primary" },
        { label: "Payées",          counter: payees,      suffix: "",   decimals: 0, icon: "ri-checkbox-circle-line",  color: "success" },
        { label: "Impayées",        counter: impayees,    suffix: "",   decimals: 0, icon: "ri-close-circle-line",     color: "danger"  },
        { label: "Total impayé",    counter: totalImpaye, suffix: " €", decimals: 2, icon: "ri-money-euro-circle-line", color: "warning" },
    ];

    /* ── filtre ── */
    const filtered = useMemo(() => {
        const q = search.toLowerCase();
        if (!q) return invoices;
        return invoices.filter(x =>
            x.ref.toLowerCase().includes(q) ||
            formatDate(x.datef).includes(q)
        );
    }, [invoices, search]);

    /* ── loading ── */
    if (loading) {
        return (
            <div className="page-content d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
                <div className="text-center">
                    <Spinner style={{ width: "3rem", height: "3rem", color: "#0066A1" }} />
                    <p className="text-muted mt-3 fw-medium">Chargement de vos factures…</p>
                </div>
            </div>
        );
    }

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb title="Mes Factures" pageTitle="Espace Client" />

                    {error && (
                        <Alert color="danger" className="mb-4">
                            <i className="ri-error-warning-line me-2"></i>{error}
                        </Alert>
                    )}

                    {/* ── Widgets KPI ── */}
                    <Row>
                        {widgets.map((w, i) => (
                            <Col xxl={3} sm={6} key={i}>
                                <Card className="card-animate">
                                    <CardBody>
                                        <div className="d-flex justify-content-between">
                                            <div>
                                                <p className="fw-medium text-muted mb-0">{w.label}</p>
                                                <h2 className="mt-4 ff-secondary fw-semibold">
                                                    <CountUp
                                                        start={0}
                                                        end={w.counter}
                                                        decimals={w.decimals}
                                                        suffix={w.suffix}
                                                        separator=" "
                                                        duration={2}
                                                    />
                                                </h2>
                                            </div>
                                            <div className="avatar-sm flex-shrink-0">
                                                <span className={`avatar-title rounded-circle fs-4 bg-${w.color}-subtle text-${w.color}`}>
                                                    <i className={w.icon}></i>
                                                </span>
                                            </div>
                                        </div>
                                    </CardBody>
                                </Card>
                            </Col>
                        ))}
                    </Row>

                    {/* ── Tableau ── */}
                    <Row>
                        <Col lg={12}>
                            <Card id="invoiceList">
                                <CardHeader className="border-0">
                                    <div className="d-flex align-items-center gap-3">
                                        <h5 className="card-title mb-0 flex-grow-1">Factures</h5>

                                        {/* Filtre année */}
                                        <UncontrolledDropdown>
                                            <DropdownToggle tag="button" className="btn btn-soft-secondary btn-sm">
                                                <i className="ri-calendar-line me-1"></i>{year}
                                                <i className="ri-arrow-down-s-line ms-1"></i>
                                            </DropdownToggle>
                                            <DropdownMenu end style={{ maxHeight: 300, overflowY: "auto" }}>
                                                {years.map(y => (
                                                    <DropdownItem
                                                        key={y}
                                                        active={year === y.toString()}
                                                        onClick={() => changeYear(y.toString())}
                                                    >
                                                        {y}
                                                    </DropdownItem>
                                                ))}
                                            </DropdownMenu>
                                        </UncontrolledDropdown>

                                        {/* Recherche */}
                                        <div className="search-box">
                                            <Input
                                                type="text"
                                                className="form-control search"
                                                placeholder="Rechercher une facture…"
                                                value={search}
                                                onChange={e => setSearch(e.target.value)}
                                                style={{ minWidth: 220 }}
                                            />
                                            <i className="ri-search-line search-icon"></i>
                                        </div>
                                    </div>
                                </CardHeader>

                                <CardBody className="pt-0">
                                    {filtered.length === 0 ? (
                                        <div className="text-center py-5">
                                            <div className="avatar-md mx-auto mb-3">
                                                <span className="avatar-title rounded-circle bg-primary-subtle text-primary fs-3">
                                                    <i className="ri-file-list-3-line"></i>
                                                </span>
                                            </div>
                                            <h5 className="mt-2">Aucune facture</h5>
                                            <p className="text-muted">
                                                {search
                                                    ? "Aucun résultat pour cette recherche."
                                                    : `Aucune facture trouvée pour ${year}.`}
                                            </p>
                                        </div>
                                    ) : (
                                        <div className="table-responsive table-card mb-1">
                                            <table className="table align-middle table-nowrap mb-0">
                                                <thead className="text-muted text-uppercase">
                                                    <tr>
                                                        <th>Référence</th>
                                                        <th>Date</th>
                                                        <th className="text-end">Montant HT</th>
                                                        <th className="text-end">TVA</th>
                                                        <th className="text-end">Total TTC</th>
                                                        <th className="text-center">Statut</th>
                                                        <th className="text-center">PDF</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {filtered.map(inv => (
                                                        <tr key={inv.facture_id}>
                                                            <td>
                                                                <span className="fw-semibold link-primary">
                                                                    <i className="ri-file-text-line me-1 text-muted"></i>
                                                                    {inv.ref}
                                                                </span>
                                                            </td>
                                                            <td className="text-muted">{formatDate(inv.datef)}</td>
                                                            <td className="text-end text-muted">{formatEur(inv.total_ht)}</td>
                                                            <td className="text-end text-muted">{formatEur(inv.total_tva)}</td>
                                                            <td className="text-end fw-semibold">{formatEur(inv.total_ttc)}</td>
                                                            <td className="text-center">
                                                                {inv.paye === "1" ? (
                                                                    <Badge className="bg-success-subtle text-success text-uppercase">
                                                                        <i className="ri-checkbox-circle-fill me-1"></i>Payée
                                                                    </Badge>
                                                                ) : (
                                                                    <Badge className="bg-warning-subtle text-warning text-uppercase">
                                                                        <i className="ri-time-fill me-1"></i>Impayée
                                                                    </Badge>
                                                                )}
                                                            </td>
                                                            <td className="text-center">
                                                                {inv.pdf ? (
                                                                    <button
                                                                        className="btn btn-soft-primary btn-sm"
                                                                        onClick={() => openPdf(inv)}
                                                                        title="Voir le PDF"
                                                                    >
                                                                        <i className="ri-file-pdf-line"></i>
                                                                    </button>
                                                                ) : (
                                                                    <span className="text-muted">—</span>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>

                </Container>
            </div>

            {/* ── Modal PDF ── */}
            <Modal isOpen={modal} toggle={() => setModal(false)} size="xl" centered>
                <ModalHeader
                    toggle={() => setModal(false)}
                    className="border-bottom-dashed"
                >
                    <span className="fw-semibold">
                        <i className="ri-file-pdf-line me-2 text-danger"></i>
                        Facture {selectedInvoice?.ref}
                    </span>
                </ModalHeader>
                <ModalBody className="p-3">
                    {selectedInvoice?.pdf ? (
                        <iframe
                            src={selectedInvoice.pdf}
                            title={`Facture ${selectedInvoice.ref}`}
                            style={{ width: "100%", height: "75vh", border: "none", borderRadius: 8 }}
                        />
                    ) : (
                        <div className="text-center py-5">
                            <div className="avatar-xl mx-auto mb-4">
                                <span className="avatar-title rounded-circle bg-warning-subtle text-warning fs-1">
                                    <i className="ri-file-warning-line"></i>
                                </span>
                            </div>
                            <h5>PDF non disponible</h5>
                            <p className="text-muted">Le fichier PDF de cette facture n'est pas disponible.</p>
                        </div>
                    )}
                </ModalBody>
            </Modal>
        </React.Fragment>
    );
};

export default MesFactures;
